import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './Lessons.css';
import LibrarySearchBar from '../components/LibrarySearchBar.jsx';
import TopicsScroller from '../components/TopicsScroller.jsx';
import LibraryFilters from '../components/LibraryFilters.jsx';
import LessonCard from '../components/LessonCard.jsx';
import LessonActionsModal from '../components/LessonActionsModal.jsx';
import LessonPreviewModal from '../components/LessonPreviewModal.jsx';
import PublishModal from '../components/PublishModal.jsx';
import UnderConstructionNotice from '../components/UnderConstructionNotice.jsx';
import defaultCover from '../assets/novas-banner-template.jpeg';
import { getLibraryLessons, getPersonalLessons, seedLibraryIfEmpty, reassignSeededPublicTemplatesToSystem, migrateStudentInstancesToLibrary, getLessonById } from '../assets/data';
import { getLessons } from '../utils/lessonsService';

const lessonsData = [
  {
    id: 1,
    title: 'Business Negotiations',
    description: 'Mastering the art of persuasion and deal-making in a corporate setting.',
    tags: ['Business English', 'Advanced', 'Text'],
    imageUrl: 'https://images.unsplash.com/photo-1556761175-5973dc0f32e7?q=80&w=2232&auto=format&fit=crop',
  },
  {
    id: 2,
    title: 'Travel & Adventure',
    description: 'Vocabulary and phrases for booking flights, hotels, and navigating a new city.',
    tags: ['Travel', 'Beginner', 'Video'],
    imageUrl: 'https://images.unsplash.com/photo-1506744038136-46273834b3fb?q=80&w=2070&auto=format&fit=crop',
  },
  {
    id: 3,
    title: 'Idiomatic Expressions',
    description: 'Dive deep into common idioms to make your speech more natural.',
    tags: ['Vocabulary', 'Intermediate', 'Audio'],
    imageUrl: 'https://images.unsplash.com/photo-1517420704952-d9f39e95b43e?q=80&w=1964&auto=format&fit=crop',
  },
  {
    id: 4,
    title: 'Present Perfect vs. Past Simple',
    description: 'A clear breakdown of when to use these tricky tenses.',
    tags: ['Grammar', 'Intermediate', 'Text'],
    imageUrl: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?q=80&w=2070&auto=format&fit=crop',
  },
  {
    id: 5,
    title: 'Describing People',
    description: 'Learn adjectives for personality and appearance.',
    tags: ['Vocabulary', 'Beginner', 'Video'],
    imageUrl: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?q=80&w=2070&auto=format&fit=crop',
  },
  {
    id: 6,
    title: 'Formal Emails',
    description: 'How to write professional emails for work or academic purposes.',
    tags: ['Writing', 'Advanced', 'Text'],
    imageUrl: 'https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?q=80&w=2070&auto=format&fit=crop',
  },
  {
    id: 7,
    title: 'Everyday Conversations',
    description: 'Practice common scenarios like ordering food or asking for directions.',
    tags: ['Speaking', 'Beginner', 'Audio'],
    imageUrl: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?q=80&w=2070&auto=format&fit=crop',
  },
  {
    id: 8,
    title: 'The Art of Storytelling',
    description: 'Techniques for engaging your audience with compelling narratives.',
    tags: ['Creative', 'Advanced', 'Video'],
    imageUrl: 'https://images.unsplash.com/photo-1532187863486-abf9dbad1b69?q=80&w=1974&auto=format&fit=crop',
  },
  {
    id: 9,
    title: 'Phrasal Verbs',
    description: 'Tackle one of the most challenging parts of English vocabulary.',
    tags: ['Vocabulary', 'Intermediate', 'Text'],
    imageUrl: 'https://images.unsplash.com/photo-1481627834876-b7833e8f5570?q=80&w=2070&auto=format&fit=crop',
  },
  {
    id: 10,
    title: 'Current Events Discussion',
    description: 'Discuss recent news topics to improve fluency and critical thinking.',
    tags: ['Speaking', 'Advanced', 'Audio'],
    imageUrl: 'https://images.unsplash.com/photo-1504711434969-e33886168f5c?q=80&w=2070&auto=format&fit=crop',
  },
  {
    id: 11,
    title: 'Food & Cooking',
    description: 'Vocabulary for ingredients, recipes, and dining out.',
    tags: ['Lifestyle', 'Beginner', 'Video'],
    imageUrl: 'https://images.unsplash.com/photo-1571091718767-18b5b1457add?q=80&w=2072&auto=format&fit=crop',
  },
  {
    id: 12,
    title: 'Job Interview Prep',
    description: 'Prepare for common interview questions and make a great impression.',
    tags: ['Career', 'Intermediate', 'Text'],
    imageUrl: 'https://images.unsplash.com/photo-1551836022-deb4988cc6c0?q=80&w=2070&auto=format&fit=crop',
  },
];

function Lessons() {
  const navigate = useNavigate();
  const [filteredLessons, setFilteredLessons] = useState(lessonsData);
  const [level, setLevel] = useState('all');
  const [component, setComponent] = useState('all');
  const [category, setCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [mode, setMode] = useState('global'); // global | personal
  const [topicsSel, setTopicsSel] = useState([]);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' | 'list'
  const [filters, setFilters] = useState({ cefr: [], mediums: [], price: undefined, sort: 'newest', ownership: undefined, studentId: '' });
  const [libraryVersion, setLibraryVersion] = useState(0);
  const [actionsOpen, setActionsOpen] = useState(false);
  const [actionsLesson, setActionsLesson] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [publishOpen, setPublishOpen] = useState(false);

  useEffect(() => {
    const bump = () => setLibraryVersion(v => v + 1);
    window.addEventListener('libraryUpdated', bump);
    window.addEventListener('lessonDataUpdated', bump);
    return () => {
      window.removeEventListener('libraryUpdated', bump);
      window.removeEventListener('lessonDataUpdated', bump);
    };
  }, []);

  useEffect(() => {
    // pick source based on mode
    const currentTeacherId = localStorage.getItem('novas-current-teacher') || 'teacher-1';
    // Seed demo data on first run if library empty
    try {
      const seeded = seedLibraryIfEmpty(lessonsData, currentTeacherId);
      // Ensure public templates seeded as authored by teacher are reassigned to 'system'
      if (seeded) reassignSeededPublicTemplatesToSystem(currentTeacherId);
      // Also migrate existing student lessons into personal private templates (one-time)
      migrateStudentInstancesToLibrary(currentTeacherId);
    } catch (_) {}
    
    const loadLessons = async () => {
      let source = [];
      if (mode === 'personal') {
        // Load lessons from Supabase for personal mode to get real creation dates
        try {
          const supabaseLessons = await getLessons();
          console.log('📚 Lessons.js - Loaded from Supabase:', supabaseLessons.length, 'lessons');
          supabaseLessons.forEach((l, i) => {
            console.log(`  Lesson ${i + 1}:`, {
              id: l.id,
              title: l.title,
              createdAt: l.createdAt,
              created_at: l.created_at
            });
          });
          
          // Also get lessons from library
          const libraryLessons = getPersonalLessons({ teacherId: currentTeacherId });
          console.log('📚 Lessons.js - Loaded from library:', libraryLessons.length, 'lessons');
          
          // Merge: prioritize Supabase lessons (they have real dates), add library lessons that aren't duplicates
          const supabaseIds = new Set(supabaseLessons.map(l => l.id));
          const uniqueLibraryLessons = libraryLessons.filter(l => !supabaseIds.has(l.id) && !supabaseIds.has(l.originalId));
          
          source = [...supabaseLessons, ...uniqueLibraryLessons];
        } catch (error) {
          console.error('Error loading lessons from Supabase:', error);
          // Fallback to library only
          source = getPersonalLessons({ teacherId: currentTeacherId });
        }
      } else {
        // global: templates/public; fallback to mock if library empty
        const lib = getLibraryLessons().filter(l => l.isTemplate && (l.visibility || 'public') === 'public');
        source = lib.length ? lib : lessonsData;
      }

    const mapCEFRToLabel = (code) => {
      if (!code) return '';
      const c = String(code).toUpperCase();
      if (c === 'A1' || c === 'A2') return 'Beginner';
      if (c === 'B1' || c === 'B2') return 'Intermediate';
      if (c === 'C1' || c === 'C2') return 'Advanced';
      return c;
    };
    const computeTags = (lesson) => {
      if (Array.isArray(lesson.tags) && lesson.tags.length) return lesson.tags;
      const topics = Array.isArray(lesson.topics) ? lesson.topics : [];
      const cefr = Array.isArray(lesson.cefrLevels) ? Array.from(new Set(lesson.cefrLevels.map(mapCEFRToLabel))) : [];
      const medium = lesson.medium ? [String(lesson.medium).charAt(0).toUpperCase() + String(lesson.medium).slice(1)] : [];
      return [...topics, ...cefr, ...medium].filter(Boolean);
    };

    let result = source;

    if (searchTerm) {
      result = result.filter(lesson =>
        lesson.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        lesson.description.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (level !== 'all') {
      result = result.filter(lesson => computeTags(lesson).includes(level));
    }

    if (component !== 'all') {
      result = result.filter(lesson => computeTags(lesson).includes(component));
    }

    if (category !== 'all') {
      result = result.filter(lesson => computeTags(lesson).map(t => (t || '').toLowerCase()).includes(category.toLowerCase()));
    }

    if (topicsSel.length > 0) {
      result = result.filter(lesson => {
        const tags = computeTags(lesson);
        return topicsSel.some(t => tags.includes(t));
      });
    }

    // Apply LibraryFilters selections
    if (filters.cefr && filters.cefr.length > 0) {
      const mappedLevels = filters.cefr.map(mapCEFRToLabel);
      result = result.filter(lesson => mappedLevels.some(lvl => computeTags(lesson).includes(lvl)));
    }

    if (filters.mediums && filters.mediums.length > 0) {
      // mediums in data are capitalized: Video, Text, Audio
      const mapped = filters.mediums.map(m => (m || '').toString().toLowerCase()).map(m => ({ video:'Video', text:'Text', audio:'Audio' }[m] || m));
      result = result.filter(lesson => mapped.some(m => computeTags(lesson).includes(m)));
    }

    // Personal: ownership filter including 'published'
    if (mode === 'personal' && filters.ownership) {
      const me = localStorage.getItem('novas-current-teacher') || 'teacher-1';
      if (filters.ownership === 'mine') {
        result = result.filter(l => String(l.authorId || '') === String(me));
      } else if (filters.ownership === 'purchased') {
        result = result.filter(l => Array.isArray(l.purchasedBy) && l.purchasedBy.includes(String(me)));
      } else if (filters.ownership === 'published') {
        result = result.filter(l => l.status === 'published' || l.moderation === 'approved' || l.approved === true || l.isApproved === true);
      } else if (filters.ownership === 'studentHistory') {
        const historyType = filters.historyType || 'student';
        if (historyType === 'student') {
          const sid = (filters.studentId || '').trim();
          if (sid) result = result.filter(l => String(l.studentId || '') === sid);
        } else if (historyType === 'group') {
          const gid = (filters.groupId || '').trim();
          if (gid) result = result.filter(l => String(l.groupId || '') === gid);
        }
      }
    }

    if (filters.sort) {
      if (filters.sort === 'popular') {
        // demo: sort by title length desc
        result = [...result].sort((a,b) => (b.title?.length||0) - (a.title?.length||0));
      } else if (filters.sort === 'price_low' || filters.sort === 'price_high') {
        // no prices in mock; keep stable
        result = [...result];
      } else {
        // newest: sort by creation date
        result = [...result].sort((a, b) => {
          const dateA = new Date(a.createdAt || a.created_at || 0);
          const dateB = new Date(b.createdAt || b.created_at || 0);
          return dateB - dateA; // newest first
        });
      }
    }

    setFilteredLessons(result);
    };
    
    loadLessons();
  }, [level, component, category, searchTerm, topicsSel, mode, filters, libraryVersion]);

  const handleClearFilters = () => {
    setLevel('all');
    setComponent('all');
    setCategory('all');
    setSearchTerm('');
  };

  // eslint-disable-next-line no-unused-vars
  const allCategories = [...new Set(lessonsData.flatMap(l => l.tags))].filter(t => !['Beginner', 'Intermediate', 'Advanced', 'Video', 'Text', 'Audio'].includes(t));
  // eslint-disable-next-line no-unused-vars
  const levels = ['all', 'Beginner', 'Intermediate', 'Advanced'];
  // eslint-disable-next-line no-unused-vars
  const components = ['all', 'Video', 'Text', 'Audio'];

  const allTopics = [
    'Business English','Travel & Adventure','Vocabulary','Writing','Speaking','Grammar','Creative','Lifestyle','Career','IELTS','TOEFL','General English','Listening','Reading','Pronunciation','Phrasal Verbs','Idioms','Exam Skills'
  ];

  const toggleTopic = (t) => {
    setTopicsSel(prev => prev.includes(t) ? prev.filter(x => x !== t) : [...prev, t]);
  };

  const openActionsFor = (lesson) => {
    if (mode !== 'personal') return;
    setActionsLesson(lesson);
    setActionsOpen(true);
  };

  const handlePreview = () => {
    const l = actionsLesson || {};
    let targetId = undefined;
    if (l.originalId) {
      targetId = String(l.originalId);
    } else if (typeof l.id === 'string' && l.id.startsWith('tmpl-')) {
      targetId = l.id.slice(5);
    } else if (l.id) {
      targetId = String(l.id);
    }
    let data = l;
    if (targetId && targetId !== l.id) {
      try {
        const loaded = getLessonById(targetId);
        if (loaded) {
          data = { ...loaded, studentId: l.studentId || loaded.studentId };
        }
      } catch (_) {}
    }
    setPreviewData(data);
    setActionsOpen(false);
    setPreviewOpen(true);
  };

  const handleEdit = () => {
    const l = actionsLesson || {};
    let targetId = undefined;
    if (l.originalId) {
      targetId = String(l.originalId);
    } else if (typeof l.id === 'string' && l.id.startsWith('tmpl-')) {
      targetId = l.id.slice(5);
    } else if (l.id) {
      targetId = String(l.id);
    }
    setActionsOpen(false);
    if (targetId) {
      const studentQs = l.studentId ? `?studentId=${encodeURIComponent(String(l.studentId))}` : '';
      navigate(`/lessons/${targetId}${studentQs}`);
    }
  };

  const handlePublish = () => {
    setActionsOpen(false);
    setPublishOpen(true);
  };

  return (
    <div className="lessons-library-page">
      <UnderConstructionNotice section="Lessons" />
      <div className="library-banner">
        <img 
          src={process.env.PUBLIC_URL + '/LessonLibrary.jpg'} 
          alt="Lesson Library" 
          className="banner-image" 
          onError={(e) => { e.currentTarget.src = defaultCover; }}
        />
        <div className="banner-overlay"></div>
        <div className="banner-content">
          <h1>Lesson Library</h1>
          <p>Find the perfect lesson for your next class.</p>
        </div>
      </div>

      <div className="filter-panel">
        <div className="top-row">
          <LibrarySearchBar value={searchTerm} onChange={setSearchTerm} mode={mode} onModeChange={setMode} />
        </div>

        <TopicsScroller topics={allTopics} selected={topicsSel} onToggle={toggleTopic} />
      </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 180px', gap: 16 }}>
          <div>
            <div style={{ position: 'relative' }}>
              <div className="view-toggle" style={{ position: 'absolute', top: -35, left: 0, display: 'flex', gap: 0 }}>
                <button
                  className={`view-toggle-btn ${viewMode === 'grid' ? 'active' : ''}`}
                  onClick={() => setViewMode('grid')}
                  aria-label="Grid view"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="3" width="8" height="8" rx="1.5"/>
                    <rect x="13" y="3" width="8" height="8" rx="1.5"/>
                    <rect x="3" y="13" width="8" height="8" rx="1.5"/>
                    <rect x="13" y="13" width="8" height="8" rx="1.5"/>
                  </svg>
                </button>
                <button
                  className={`view-toggle-btn ${viewMode === 'list' ? 'active' : ''}`}
                  onClick={() => setViewMode('list')}
                  aria-label="List view"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="4" y1="6" x2="20" y2="6"/>
                    <line x1="4" y1="12" x2="20" y2="12"/>
                    <line x1="4" y1="18" x2="20" y2="18"/>
                  </svg>
                </button>
              </div>
              {viewMode === 'grid' ? (
                <div className="lessons-grid">
                  {filteredLessons.length > 0 ? filteredLessons.map(lesson => (
                    <div key={lesson.id} onClick={() => openActionsFor(lesson)}>
                      <LessonCard lesson={lesson} />
                    </div>
                  )) : (
                    <div className="no-results-message">
                      <h3>No lessons found</h3>
                      <p>Try adjusting your search or clearing the filters.</p>
                      <button onClick={handleClearFilters}>Clear All Filters</button>
                    </div>
                  )}
                </div>
              ) : (
                <div className="lessons-list-container">
                  {filteredLessons.length > 0 ? (
                    <div className="lessons-table-container">
                      <table>
                        <thead>
                          <tr>
                            <th className="col-title">Title</th>
                            <th className="col-topics">Topics</th>
                            <th className="col-cefr">CEFR</th>
                            <th className="col-medium">Medium</th>
                            <th className="col-student">Student</th>
                            <th className="col-date">Date</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredLessons.map((l) => {
                            const topics = Array.isArray(l.topics) ? l.topics.join(', ') : '';
                            const cefr = Array.isArray(l.cefrLevels) ? l.cefrLevels.join(', ') : '';
                            const medium = l.medium ? String(l.medium) : '';
                            const student = l.studentName || (l.student && (l.student.name || l.student.title)) || (l.studentId || '');
                            const banners = l && typeof l.docBanners === 'object' && l.docBanners !== null ? Object.values(l.docBanners).filter(Boolean) : [];
                            const cover = l?.cover || l?.bannerImage || l?.imageUrl || (banners.length ? banners[0] : defaultCover);
                            
                            // Format creation date
                            const formatDate = (dateString) => {
                              if (!dateString) return '-';
                              try {
                                const date = new Date(dateString);
                                if (isNaN(date.getTime())) return '-';
                                return date.toLocaleDateString('en-US', { 
                                  year: 'numeric', 
                                  month: 'short', 
                                  day: 'numeric' 
                                });
                              } catch {
                                return '-';
                              }
                            };
                            // Use createdAt for date column (creation date, not schedule date)
                            // Priority: createdAt > created_at > (date if not a schedule entry)
                            const creationDate = l.createdAt || l.created_at || (l.date && !l.from && !l.to && !l.timeFrom && !l.timeTo ? l.date : null);
                            const lessonDate = formatDate(creationDate);
                            
                             return (
                              <tr key={l.id} onClick={() => openActionsFor(l)}>
                                <td className="lesson-name-cell col-title">
                                  <img className="lesson-icon" src={cover} alt="" />
                                  <div>
                                    <div className="lesson-title">{l.title}</div>
                                    <div className="lesson-meta">{l.description || ''}</div>
                                  </div>
                                </td>
                                <td className="col-topics">{topics}</td>
                                <td className="col-cefr">{cefr}</td>
                                <td className="col-medium">{medium}</td>
                                <td className="col-student">{student}</td>
                                <td className="col-date">{lessonDate}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="no-results-message">
                      <h3>No lessons found</h3>
                      <p>Try adjusting your search or clearing the filters.</p>
                      <button onClick={handleClearFilters}>Clear All Filters</button>
                    </div>
                  )}
                </div>
              )}
          </div>
          </div>
        <LibraryFilters mode={mode} filters={filters} onChange={setFilters} />
        {/* Modals: actions on hover in personal mode */}
        <LessonActionsModal
          open={actionsOpen && mode === 'personal' && !!actionsLesson}
          onClose={() => setActionsOpen(false)}
          lesson={actionsLesson}
          onPreview={handlePreview}
          onEdit={handleEdit}
          onPublish={handlePublish}
        />
        <LessonPreviewModal
          open={previewOpen}
          onClose={() => setPreviewOpen(false)}
          lesson={previewData || actionsLesson}
        />
        <PublishModal
          open={publishOpen}
          onClose={() => setPublishOpen(false)}
          lessonDraft={actionsLesson}
        />
      </div>
    </div>
  );
}

export default Lessons; 